/* eslint-disable @typescript-eslint/no-explicit-any -- U2000Input* attend FormStateHandlers générique large */
import type { TFunction } from 'i18next';
import U2000CheckBox from '../../../u2000Core/components/formControls/checkbox/U2000CheckBox';
import U2000ComboBox from '../../../u2000Core/components/formControls/combobox/U2000ComboBox';
import U2000InfoTooltip from '../../../u2000Core/components/formControls/feedback/U2000InfoTooltip';
import U2000InputText from '../../../u2000Core/components/formControls/input/U2000InputText';
import type { U2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from '../../../u2000Core/reducer/formReducer/U2000FormState';
import { NS } from '../../U3202';
import type { U3202OrgProviderConfigFormData } from '../../U3202Service';

interface U3202OrgProviderConfigBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3202OrgProviderConfigFormData>;
  t: TFunction;
}

/** Fournisseurs LLM supportés pour la découverte automatique (BL-0148). */
const U3202_SUPPORTED_PROVIDER_OPTIONS = [
  { code: 'anthropic', name: 'Anthropic' },
  { code: 'openai', name: 'OpenAI' },
];

// Un seul rendu (pattern U2101) : mêmes champs et même mise en page à l'ajout et en
// consultation. À la création on sélectionne un fournisseur (ComboBox) ; l'URL API sert
// d'URL par défaut du fournisseur. Les modèles se gèrent dans l'onglet « Modèles ».
function U3202OrgProviderConfigBody({ formStateHandlers, t }: U3202OrgProviderConfigBodyProps) {
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const isCreate = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD;
  const data = formStateHandlers.getData();

  const apiKeyPlaceholder =
    !data?.apiKeyTouched && !data?.apiKey && data?.hasExistingApiKey
      ? t('hintApiKeyConfigured', { ns: NS })
      : undefined;

  const apiKeyFormHandlers: U2000FormStateHandlers<U3202OrgProviderConfigFormData> = {
    ...formStateHandlers,
    onChange: (e, value) => {
      if (e.target.id !== 'apiKey') {
        formStateHandlers.onChange?.(e, value);
        return;
      }
      const current = formStateHandlers.getData();
      const updated: U3202OrgProviderConfigFormData = {
        ...current,
        apiKey: String(value ?? ''),
        apiKeyTouched: true,
      };
      formStateHandlers.setData(updated);
      const validateForm = formStateHandlers.getBusinessHandlers()?.validateForm;
      if (validateForm) {
        formStateHandlers.setErrors(validateForm(updated));
      }
    },
  };

  const providerComboHandlers: U2000FormStateHandlers<U3202OrgProviderConfigFormData> = {
    ...formStateHandlers,
    onChange: (e, value) => {
      if (e.target.id !== 'newProviderCode') {
        formStateHandlers.onChange?.(e, value);
        return;
      }
      const selected = U3202_SUPPORTED_PROVIDER_OPTIONS.find((option) => option.code === value);
      const current = formStateHandlers.getData();
      const updated: U3202OrgProviderConfigFormData = {
        ...current,
        newProviderCode: selected?.code ?? String(value ?? ''),
        newProviderName: selected?.name ?? '',
      };
      formStateHandlers.setData(updated);
      const validateForm = formStateHandlers.getBusinessHandlers()?.validateForm;
      if (validateForm) {
        formStateHandlers.setErrors(validateForm(updated));
      }
    },
  };

  const providerDisplayHandlers = {
    ...formStateHandlers,
    getData: () => ({
      ...formStateHandlers.getData(),
      providerDisplay: data?.providerName ? `${data.providerName} (${data.providerCode})` : '',
    }),
    onChange: () => undefined,
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {isCreate ? (
        <U2000ComboBox
          formStateHandlers={providerComboHandlers as any}
          fieldName="newProviderCode"
          label={t('catalogWizard.lblProviderSelect', { ns: NS })}
          data={U3202_SUPPORTED_PROVIDER_OPTIONS}
          keyField="code"
          descField="name"
          required
        />
      ) : (
        <U2000InputText
          formStateHandlers={providerDisplayHandlers as any}
          fieldName="providerDisplay"
          label={t('lblProvider', { ns: NS })}
          readonly
        />
      )}

      <U2000InputText
        formStateHandlers={apiKeyFormHandlers as any}
        fieldName="apiKey"
        label={t('lblApiKey', { ns: NS })}
        type="password"
        placeholder={apiKeyPlaceholder}
        readonly={readonly}
        required={isCreate || !data?.hasExistingApiKey}
      />

      <U2000InputText
        formStateHandlers={formStateHandlers as any}
        fieldName="apiUrl"
        label={t('lblApiUrl', { ns: NS })}
        readonly={readonly}
      />

      <div className="flex items-center gap-2 md:col-span-2">
        <U2000CheckBox
          formStateHandlers={formStateHandlers as any}
          fieldName="showThinking"
          label={t('showThinking', { ns: NS })}
          readonly={readonly}
        />
        <U2000InfoTooltip message={t('showThinking.help', { ns: NS })} align="left" />
      </div>
    </div>
  );
}

export default U3202OrgProviderConfigBody;
